import React, { useState, useEffect } from "react";
import styled from "styled-components";
import axios from "axios";
import CampaignHighlights from "./CampaignHighlights";
import BenchmarkComparison from "./BenchmarkComparison";
import CampaignRecommendations from "./CampaignRecommendations";
import CallToAction from "./CallToAction";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const DashboardContainer = styled.div`
  margin-top: 20px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  color: var(--text-primary, #333);
  margin: 30px 0 15px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-color, #eee);
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const MetricCard = styled.div`
  background-color: white;
  border-radius: 0;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const MetricValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #6ed216;
  margin-bottom: 5px;
`;

const MetricLabel = styled.div`
  font-size: 14px;
  color: #64748b;
`;

const ContentSection = styled.div`
  background-color: var(--card-bg, white);
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`;

const ContentTitle = styled.h3`
  font-size: 16px;
  color: #333;
  margin-bottom: 10px;
`;

const ContentBox = styled.div`
  border: 1px solid var(--border-color, #eee);
  padding: 15px;
  border-radius: 4px;
  background-color: var(--content-bg, #f9f9f9);
  white-space: pre-wrap;
  font-family: monospace;
  max-height: 300px;
  overflow-y: auto;
  color: var(--text-primary, black);
`;

const EmailPreview = styled.div`
  border: 1px solid var(--border-color, #eee);
  padding: 15px;
  border-radius: 4px;
  background-color: var(--email-preview-bg, white);
  max-height: 500px;
  overflow-y: auto;
  color: var(--text-primary, black);
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: var(--text-secondary, #666);
  font-style: italic;
`;

const ChartsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const ChartCard = styled.div`
  background-color: white;
  border-radius: 0;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  height: 300px;
`;

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8"];

const EditForm = styled.div`
  background-color: #fff3cd;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid #ffeaa7;
  border-radius: 8px;
`;

const EditSection = styled.div`
  margin-bottom: 20px;
`;

const EditTitle = styled.h4`
  color: #333;
  margin-bottom: 15px;
  font-size: 16px;
`;

const EditGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
`;

const EditField = styled.div`
  display: flex;
  flex-direction: column;
`;

const EditLabel = styled.label`
  font-size: 14px;
  color: #555;
  margin-bottom: 5px;
  font-weight: 500;
`;

const EditInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  &:focus {
    outline: none;
    border-color: #6ed216;
    box-shadow: 0 0 0 2px rgba(110, 210, 22, 0.2);
  }
`;

const EditActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const EditButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s;
`;

const SaveButton = styled(EditButton)`
  background-color: #6ed216;
  color: white;
  &:hover {
    background-color: #5cb012;
  }
`;

const CancelButton = styled(EditButton)`
  background-color: #94a3b8;
  color: white;
  &:hover {
    background-color: #7c8a9a;
  }
`;

function SmsEmailDashboard({ reportData, isEditing, onSave, onCancel, user, onDataUpdate, setReportData }) {
  const [editedSmsData, setEditedSmsData] = useState(null);
  const [editedEmailData, setEditedEmailData] = useState(null);
  const [saving, setSaving] = useState(false);

  const formatNumber = (num) => {
    return parseInt(num || 0).toLocaleString('fr-FR');
  };

  useEffect(() => {
    if (reportData) {
      setEditedSmsData(reportData.smsData ? { ...reportData.smsData } : null);
      setEditedEmailData(reportData.emailData ? { ...reportData.emailData } : null);
    }
  }, [reportData]);

  if (!reportData || (!reportData.smsData && !reportData.emailData)) {
    return (
      <NoDataMessage>Aucune donnée disponible pour ce rapport</NoDataMessage>
    );
  }

  const { smsData, emailData, smsContent, emailHtml, emailImage } = reportData;

  const handleSmsDataChange = (field, value) => {
    setEditedSmsData(prev => ({ ...prev, [field]: value }));
  };

  const handleEmailDataChange = (field, value) => {
    setEditedEmailData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "https://report-campaign.sirdata.io";
      const token = localStorage.getItem("token");
      
      const updateData = {};
      if (editedSmsData) updateData.smsData = editedSmsData;
      if (editedEmailData) updateData.emailData = editedEmailData;
      if (reportData.calendarLink !== undefined) updateData.calendarLink = reportData.calendarLink;

      const response = await axios.put(`${apiUrl}/api/reports/${reportData.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (onDataUpdate && response.data.report) {
        onDataUpdate(response.data.report);
      }
      
      onSave();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Erreur lors de la sauvegarde des modifications');
    } finally {
      setSaving(false);
    }
  };

  const currentSmsData = isEditing ? editedSmsData : smsData;
  const currentEmailData = isEditing ? editedEmailData : emailData;

  // Calculate SMS delivery rate
  const smsDeliveryRate = smsData
    ? (
        (parseInt(smsData.delivered || 0) /
          parseInt(smsData.sent || 1)) *
        100
      ).toFixed(1)
    : 0;

  // Calculate SMS preview rate
  const smsPreviewRate = smsData
    ? (
        (parseInt(smsData.previews || 0) /
          parseInt(smsData.delivered || 1)) *
        100
      ).toFixed(1)
    : 0;

  // Calculate SMS click rate (clicks / previews)
  const smsClickToPreviewRate = smsData
    ? (
        (parseInt(smsData.clicks || 0) /
          parseInt(smsData.previews || 1)) *
        100
      ).toFixed(1)
    : 0;

  // Calculate Email delivery rate
  const emailDeliveryRate = emailData
    ? (
        (parseInt(emailData.delivered || 0) /
          parseInt(emailData.sent || 1)) *
        100
      ).toFixed(1)
    : 0;

  // Calculate Email open rate
  const emailOpenRate = emailData
    ? (
        (parseInt(emailData.opens || 0) /
          parseInt(emailData.delivered || 1)) *
        100
      ).toFixed(1)
    : 0;

  // Calculate Email click rate (clicks / opens)
  const emailClickToOpenRate = emailData
    ? (
        (parseInt(emailData.clicks || 0) /
          parseInt(emailData.opens || 1)) *
        100
      ).toFixed(1)
    : 0;

  // Prepare data for charts
  const prepareEmailFunnelData = () => {
    if (!emailData) return [];

    return [
      { name: "Envoyés", value: parseInt(emailData.sent || 0) },
      { name: "Délivrés", value: parseInt(emailData.delivered || 0) },
      { name: "Ouvertures", value: parseInt(emailData.opens || 0) },
      { name: "Clics", value: parseInt(emailData.clicks || 0) },
    ];
  };

  const prepareEmailRatesData = () => {
    if (!emailData) return [];

    return [
      { name: "Taux de délivrance", value: parseFloat(emailDeliveryRate) },
      { name: "Taux d'ouverture", value: parseFloat(emailOpenRate) },
      { name: "Taux de clic/ouverture", value: parseFloat(emailClickToOpenRate) },
    ];
  };

  const prepareSmsFunnelData = () => {
    if (!smsData) return [];

    return [
      { name: "Envoyés", value: parseInt(smsData.sent || 0) },
      { name: "Délivrés", value: parseInt(smsData.delivered || 0) },
      { name: "Previews", value: parseInt(smsData.previews || 0) },
      { name: "Clics", value: parseInt(smsData.clicks || 0) },
    ];
  };

  const prepareSmsRatesData = () => {
    if (!smsData) return [];

    return [
      { name: "Taux de délivrance", value: parseFloat(smsDeliveryRate) },
      { name: "Taux de preview", value: parseFloat(smsPreviewRate) },
      { name: "Taux clic/preview", value: parseFloat(smsClickToPreviewRate) },
    ];
  };

  const emailFunnelData = prepareEmailFunnelData();
  const emailRatesData = prepareEmailRatesData();
  const smsFunnelData = prepareSmsFunnelData();
  const smsRatesData = prepareSmsRatesData();

  return (
    <DashboardContainer>
      {isEditing && user ? (
        <EditForm>
          <h3>Mode Édition - Modifier les données du rapport</h3>
          
          {editedSmsData && (
            <EditSection>
              <EditTitle>📱 Données SMS</EditTitle>
              <EditGrid>
                <EditField>
                  <EditLabel>SMS envoyés</EditLabel>
                  <EditInput
                    type="number"
                    value={editedSmsData.sent || 0}
                    onChange={(e) => handleSmsDataChange('sent', e.target.value)}
                  />
                </EditField>
                <EditField>
                  <EditLabel>SMS délivrés</EditLabel>
                  <EditInput
                    type="number"
                    value={editedSmsData.delivered || 0}
                    onChange={(e) => handleSmsDataChange('delivered', e.target.value)}
                  />
                </EditField>
                <EditField>
                  <EditLabel>Previews</EditLabel>
                  <EditInput
                    type="number"
                    value={editedSmsData.previews || 0}
                    onChange={(e) => handleSmsDataChange('previews', e.target.value)}
                  />
                </EditField>
                <EditField>
                  <EditLabel>Clics</EditLabel>
                  <EditInput
                    type="number"
                    value={editedSmsData.clicks || 0}
                    onChange={(e) => handleSmsDataChange('clicks', e.target.value)}
                  />
                </EditField>
                <EditField>
                  <EditLabel>Désabonnements</EditLabel>
                  <EditInput
                    type="number"
                    value={editedSmsData.unsubscribes || 0}
                    onChange={(e) => handleSmsDataChange('unsubscribes', e.target.value)}
                  />
                </EditField>
                <EditField>
                  <EditLabel>Coût (€)</EditLabel>
                  <EditInput
                    type="number"
                    step="0.01"
                    value={editedSmsData.cost || 0}
                    onChange={(e) => handleSmsDataChange('cost', e.target.value)}
                  />
                </EditField>
              </EditGrid>
            </EditSection>
          )}

          {editedEmailData && (
            <EditSection>
              <EditTitle>📧 Données Email</EditTitle>
              <EditGrid>
                <EditField>
                  <EditLabel>Emails envoyés</EditLabel>
                  <EditInput
                    type="number"
                    value={editedEmailData.sent || 0}
                    onChange={(e) => handleEmailDataChange('sent', e.target.value)}
                  />
                </EditField>
                <EditField>
                  <EditLabel>Emails délivrés</EditLabel>
                  <EditInput
                    type="number"
                    value={editedEmailData.delivered || 0}
                    onChange={(e) => handleEmailDataChange('delivered', e.target.value)}
                  />
                </EditField>
                <EditField>
                  <EditLabel>Ouvertures</EditLabel>
                  <EditInput
                    type="number"
                    value={editedEmailData.opens || 0}
                    onChange={(e) => handleEmailDataChange('opens', e.target.value)}
                  />
                </EditField>
                <EditField>
                  <EditLabel>Clics</EditLabel>
                  <EditInput
                    type="number"
                    value={editedEmailData.clicks || 0}
                    onChange={(e) => handleEmailDataChange('clicks', e.target.value)}
                  />
                </EditField>
                <EditField>
                  <EditLabel>Désabonnements</EditLabel>
                  <EditInput
                    type="number"
                    value={editedEmailData.unsubscribes || 0}
                    onChange={(e) => handleEmailDataChange('unsubscribes', e.target.value)}
                  />
                </EditField>
                <EditField>
                  <EditLabel>Coût (€)</EditLabel>
                  <EditInput
                    type="number"
                    step="0.01"
                    value={editedEmailData.cost || 0}
                    onChange={(e) => handleEmailDataChange('cost', e.target.value)}
                  />
                </EditField>
              </EditGrid>
            </EditSection>
          )}

          <EditSection>
            <EditTitle>📅 Call to Action</EditTitle>
            <EditField>
              <EditLabel>Lien calendrier (optionnel)</EditLabel>
              <EditInput
                type="url"
                placeholder="https://calendly.com/votre-lien"
                value={reportData.calendarLink || ''}
                onChange={(e) => {
                  setReportData(prev => ({...prev, calendarLink: e.target.value}));
                }}
              />
            </EditField>
          </EditSection>

          <EditActions>
            <SaveButton onClick={handleSave} disabled={saving}>
              {saving ? 'Sauvegarde...' : '💾 Sauvegarder'}
            </SaveButton>
            <CancelButton onClick={onCancel} disabled={saving}>
              ❌ Annuler
            </CancelButton>
          </EditActions>
        </EditForm>
      ) : null}
      <CampaignHighlights smsData={smsData} emailData={emailData} />
      {smsData && (
        <>
          <SectionTitle>Rapport SMS</SectionTitle>
          <MetricsGrid>
            <MetricCard>
              <MetricValue>{formatNumber(smsData.sent)}</MetricValue>
              <MetricLabel>SMS envoyés</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{formatNumber(smsData.delivered)}</MetricValue>
              <MetricLabel>SMS délivrés</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{smsDeliveryRate}%</MetricValue>
              <MetricLabel>Taux de délivrance</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{formatNumber(smsData.previews)}</MetricValue>
              <MetricLabel>Previews</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{smsPreviewRate}%</MetricValue>
              <MetricLabel>Taux de preview</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{formatNumber(smsData.clicks)}</MetricValue>
              <MetricLabel>Clics</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{smsClickToPreviewRate}%</MetricValue>
              <MetricLabel>Taux clic/preview</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{formatNumber(smsData.unsubscribes)}</MetricValue>
              <MetricLabel>Désabonnements</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{smsData.cost || 0}€</MetricValue>
              <MetricLabel>Coût total</MetricLabel>
            </MetricCard>
          </MetricsGrid>

          <ChartsContainer>
            <ChartCard>
              <ContentTitle>Entonnoir de conversion SMS</ContentTitle>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={smsFunnelData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={100} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" fill="#4285f4" name="Nombre" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard>
              <ContentTitle>Taux de performance SMS</ContentTitle>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={smsRatesData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis
                    domain={[0, 100]}
                    label={{ value: "%", angle: -90, position: "insideLeft" }}
                  />
                  <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                  <Legend />
                  <Bar dataKey="value" fill="#00C49F" name="Taux (%)" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </ChartsContainer>
          {smsContent && (
            <ContentSection>
              <ContentTitle>Contenu du SMS</ContentTitle>
              <ContentBox>{smsContent}</ContentBox>
            </ContentSection>
          )}
        </>
      )}

      {emailData && (
        <>
          <SectionTitle>Rapport Email</SectionTitle>
          <MetricsGrid>
            <MetricCard>
              <MetricValue>{formatNumber(emailData.sent)}</MetricValue>
              <MetricLabel>Emails envoyés</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{formatNumber(emailData.delivered)}</MetricValue>
              <MetricLabel>Emails délivrés</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{emailDeliveryRate}%</MetricValue>
              <MetricLabel>Taux de délivrance</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{formatNumber(emailData.opens)}</MetricValue>
              <MetricLabel>Ouvertures</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{emailOpenRate}%</MetricValue>
              <MetricLabel>Taux d'ouverture</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{formatNumber(emailData.clicks)}</MetricValue>
              <MetricLabel>Clics</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{emailClickToOpenRate}%</MetricValue>
              <MetricLabel>Taux clic/ouverture</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{formatNumber(emailData.unsubscribes)}</MetricValue>
              <MetricLabel>Désabonnements</MetricLabel>
            </MetricCard>
            <MetricCard>
              <MetricValue>{emailData.cost || 0}€</MetricValue>
              <MetricLabel>Coût total</MetricLabel>
            </MetricCard>
          </MetricsGrid>

          <ChartsContainer>
            <ChartCard>
              <ContentTitle>Entonnoir de conversion Email</ContentTitle>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={emailFunnelData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{fontSize: 11}} />
                  <YAxis dataKey="name" type="category" width={100} tick={{fontSize: 12}} />
                  <Tooltip 
                    formatter={(value) => [formatNumber(value), 'Volume']}
                    contentStyle={{backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '4px'}}
                  />
                  <Legend />
                  <Bar dataKey="value" fill="#4285f4" name="Nombre" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard>
              <ContentTitle>🎯 Performance Email - Résultats Exceptionnels</ContentTitle>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={emailRatesData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{fontSize: 12, fontWeight: 600}} />
                  <YAxis
                    domain={[0, 100]}
                    label={{ value: "%", angle: -90, position: "insideLeft" }}
                    tick={{fontSize: 12}}
                  />
                  <Tooltip 
                    formatter={(value) => [`${value.toFixed(1)}%`, 'Performance']}
                    labelStyle={{fontWeight: 600}}
                    contentStyle={{backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px'}}
                  />
                  <Legend wrapperStyle={{paddingTop: '10px'}} />
                  <Bar 
                    dataKey="value" 
                    fill="#6ed216" 
                    name="Taux (%)"
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </ChartsContainer>

          {(emailHtml || emailImage) && (
            <ContentSection>
              <ContentTitle>Aperçu de l'Email</ContentTitle>
              {emailImage ? (
                <EmailPreview>
                  <img
                    src={emailImage}
                    alt="Email"
                    style={{
                      maxWidth: "100%",
                      height: "auto",
                      border: "1px solid #ddd",
                      borderRadius: "4px"
                    }}
                  />
                </EmailPreview>
              ) : (
                <EmailPreview dangerouslySetInnerHTML={{ __html: emailHtml }} />
              )}
            </ContentSection>
          )}
        </>
      )}

      {emailData && smsData && (
        <>
          <SectionTitle>Comparaison Email vs SMS</SectionTitle>
          <ChartsContainer>
            <ChartCard>
              <ContentTitle>Taux de délivrance</ContentTitle>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart
                  data={[
                    { name: "Email", value: parseFloat(emailDeliveryRate) },
                    { name: "SMS", value: parseFloat(smsDeliveryRate) },
                  ]}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis
                    domain={[0, 100]}
                    label={{ value: "%", angle: -90, position: "insideLeft" }}
                  />
                  <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                  <Legend />
                  <Bar dataKey="value" fill="#6ed216" name="Taux de délivrance (%)" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard>
              <ContentTitle>🚀 Taux de Clic - Performance Optimale</ContentTitle>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart
                  data={[
                    { name: "Email", value: parseFloat(emailClickToOpenRate) },
                    { name: "SMS", value: parseFloat(smsClickToPreviewRate) },
                  ]}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis
                    domain={[0, Math.max(parseFloat(emailClickToOpenRate), parseFloat(smsClickToPreviewRate)) * 1.5]}
                    label={{ value: "%", angle: -90, position: "insideLeft" }}
                  />
                  <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                  <Legend />
                  <Bar dataKey="value" fill="#6ed216" name="Taux de clic (%)" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard>
              <ContentTitle>📊 Portée de Votre Campagne</ContentTitle>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart
                  data={[
                    {
                      name: "Envoyés",
                      Email: parseInt(emailData.sent || 0),
                      SMS: parseInt(smsData.sent || 0),
                    },
                    {
                      name: "Délivrés",
                      Email: parseInt(emailData.delivered || 0),
                      SMS: parseInt(smsData.delivered || 0),
                    },
                    {
                      name: "Interactions",
                      Email: parseInt(emailData.opens || 0),
                      SMS: parseInt(smsData.previews || 0),
                    }
                  ]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="name" 
                    tick={{fontSize: 12, fontWeight: 600}}
                  />
                  <YAxis 
                    tick={{fontSize: 11}}
                    tickFormatter={(value) => formatNumber(value)}
                  />
                  <Tooltip 
                    formatter={(value, name) => [formatNumber(value), name]}
                    contentStyle={{
                      backgroundColor: '#fff', 
                      border: '2px solid #4285f4', 
                      borderRadius: '12px', 
                      boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
                    }}
                    labelStyle={{fontWeight: 700, color: '#333', fontSize: '14px'}}
                  />
                  <Legend 
                    wrapperStyle={{paddingTop: '15px', fontSize: '14px', fontWeight: 600}} 
                  />
                  <Bar 
                    dataKey="Email" 
                    fill="#6ed216" 
                    name="📧 Email"
                  />
                  <Bar 
                    dataKey="SMS" 
                    fill="#94a3b8" 
                    name="📱 SMS"
                  />

                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard>
              <ContentTitle>🎯 Engagement Généré</ContentTitle>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart
                  data={[
                    {
                      name: "Clics Email",
                      value: parseInt(emailData.clicks || 0),
                      percentage: parseFloat(emailClickToOpenRate)
                    },
                    {
                      name: "Clics SMS",
                      value: parseInt(smsData.clicks || 0),
                      percentage: parseFloat(smsClickToPreviewRate)
                    }
                  ]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="name" 
                    tick={{fontSize: 12, fontWeight: 600}}
                  />
                  <YAxis 
                    tick={{fontSize: 11}}
                    tickFormatter={(value) => formatNumber(value)}
                  />
                  <Tooltip 
                    formatter={(value, name, props) => [
                      `${formatNumber(value)} clics (${props.payload.percentage}%)`, 
                      'Engagement'
                    ]}
                    contentStyle={{
                      backgroundColor: '#fff', 
                      border: '2px solid #ff6b35', 
                      borderRadius: '12px', 
                      boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
                    }}
                    labelStyle={{fontWeight: 700, color: '#333', fontSize: '14px'}}
                  />
                  <Bar 
                    dataKey="value" 
                    fill="#6ed216" 
                    name="Clics"
                  />

                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </ChartsContainer>
        </>
      )}
      
      <BenchmarkComparison 
        smsData={smsData}
        emailData={emailData}
        smsDeliveryRate={smsDeliveryRate}
        smsPreviewRate={smsPreviewRate}
        smsClickToPreviewRate={smsClickToPreviewRate}
        emailDeliveryRate={emailDeliveryRate}
        emailOpenRate={emailOpenRate}
        emailClickToOpenRate={emailClickToOpenRate}
      />
      
      <CampaignRecommendations 
        smsData={smsData}
        emailData={emailData}
        smsClickToPreviewRate={smsClickToPreviewRate}
        emailClickToOpenRate={emailClickToOpenRate}
        emailOpenRate={emailOpenRate}
      />
      
      <CallToAction calendarLink={reportData.calendarLink} />
    </DashboardContainer>
  );
}

export default SmsEmailDashboard;
